import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useAudio } from '@context/AudioContext';
import { useCart } from '@context/CartContext';
import ImageWithFallback from '@components/common/ImageWithFallback';
import AcetatePlaceholder from '@components/common/AcetatePlaceholder';

// Animations
const rotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const equalizer = keyframes`
  0% { height: 5px; }
  50% { height: 20px; }
  100% { height: 5px; }
`;

const ripple = keyframes`
  0% { transform: scale(0.95); opacity: 1; }
  100% { transform: scale(1.3); opacity: 0; }
`;

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(255, 107, 107, 0.7); }
  70% { box-shadow: 0 0 0 10px rgba(255, 107, 107, 0); }
  100% { box-shadow: 0 0 0 0 rgba(255, 107, 107, 0); }
`;

// Hoisted constant to avoid array recreation on every render
const EQUALIZER_BAR_INDICES = [0, 1, 2, 3, 4];

const colorShift = keyframes`
  0% { filter: hue-rotate(0deg); }
  100% { filter: hue-rotate(360deg); }
`;

// Styled Components
const PlayerContainer = styled(motion.div)`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: ${({ theme }) => theme.colors.gradients.warm};
  backdrop-filter: blur(10px);
  padding: 1rem;
  z-index: ${({ theme }) => theme.zIndex.sticky};
  border-top: 1px solid ${({ theme }) => theme.colors.overlay.light};
  transition: all ${({ theme }) => theme.transitions.default};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 0.75rem;
  }
`;

const PlayerContent = styled.div`
  display: flex;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
  min-height: 60px; /* Ensure consistent height */
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

const TrackInfo = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  gap: 1rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 100%;
    justify-content: center;
  }
`;

const NowPlaying = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const TrackTitle = styled.h3`
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.fontSizes.lg};
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fontSizes.md};
  }
`;

const ArtistName = styled.p`
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const AlbumArt = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'isPlaying'
})<{ isPlaying: boolean }>`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  overflow: hidden;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  
  ${({ isPlaying }) => isPlaying && css`
    animation: ${rotate} 20s linear infinite;
  `}
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  .acetate-container {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .regular-image::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 15px;
    height: 15px;
    background-color: ${({ theme }) => theme.colors.background};
    border-radius: 50%;
    z-index: 2;
  }
  
  .regular-image::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(
      circle at center,
      transparent 30%,
      rgba(0, 0, 0, 0.5) 100%
    );
    z-index: 1;
  }
`;

const RippleEffect = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.primary}40;
  animation: ${ripple} 1.5s ease-out infinite;
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  height: 40px; /* Fixed height to ensure alignment */
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 100%;
    justify-content: center;
  }
`;

const PlayButton = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== 'isPlaying'
})<{ isPlaying: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.secondary};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: none;
  transition: all ${({ theme }) => theme.transitions.quick};
  
  ${({ isPlaying }) => isPlaying && css`
    animation: ${pulse} 2s infinite;
  `}
  
  &:hover {
    transform: scale(1.1);
    background: ${({ theme }) => theme.colors.accent1};
  }
  
  svg {
    width: 18px;
    height: 18px;
    fill: white;
  }
`;

const SkipButton = styled.button`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.overlay.light};
  color: ${({ theme }) => theme.colors.text};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: none;
  transition: all ${({ theme }) => theme.transitions.quick};
  
  &:hover {
    transform: scale(1.1);
    background: ${({ theme }) => theme.colors.overlay.medium};
  }
  
  svg {
    width: 14px;
    height: 14px;
    fill: ${({ theme }) => theme.colors.text};
  }
`;

const ProgressContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0 1rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 100%;
    padding: 0;
  }
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 4px;
  background: ${({ theme }) => theme.colors.overlay.light};
  border-radius: ${({ theme }) => theme.borderRadius.pill};
  overflow: hidden;
  cursor: pointer;
  position: relative;
`;

const Progress = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'width' && prop !== 'isPlaying'
})<{ width: number; isPlaying: boolean }>`
  height: 100%;
  width: ${({ width }) => `${width}%`};
  background: ${({ theme }) => theme.colors.gradients.primary};
  border-radius: ${({ theme }) => theme.borderRadius.pill};
  transition: width 0.1s linear;
  
  ${({ isPlaying }) => isPlaying && css`
    animation: ${colorShift} 10s linear infinite;
  `}
`;

const TimeDisplay = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textMuted};
`;

const VolumeControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    display: none;
  }
`;

const VolumeSlider = styled.input`
  -webkit-appearance: none;
  width: 80px;
  height: 4px;
  background: ${({ theme }) => theme.colors.overlay.light};
  border-radius: ${({ theme }) => theme.borderRadius.pill};
  outline: none;
  
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.primary};
    cursor: pointer;
    transition: all ${({ theme }) => theme.transitions.quick};
  }
  
  &::-webkit-slider-thumb:hover {
    transform: scale(1.2);
  }
`;

const CartButton = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== 'inCart'
})<{ inCart: boolean }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${({ inCart, theme }) => inCart ? theme.colors.accent1 : theme.colors.primary};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  margin-left: 1rem;
  border: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
  align-self: center; /* Ensure vertical alignment */
  
  svg {
    width: 20px;
    height: 20px;
    fill: white;
  }
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
    background: ${({ inCart, theme }) => inCart ? theme.colors.accent1 : theme.colors.primary};
  }
  
  &:focus {
    outline: none;
  }
`;

const VolumeIcon = styled.div`
  color: ${({ theme }) => theme.colors.textMuted};
  display: flex;
  align-items: center;
  
  svg {
    width: 16px;
    height: 16px;
    fill: currentColor;
  }
`;

const Equalizer = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'isPlaying'
})<{ isPlaying: boolean }>`
  display: flex;
  align-items: end;
  gap: 2px;
  height: 20px;
  margin-left: 1rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: none;
  }
`;

const EqualizerBar = styled.div<{ delay: number }>`
  width: 3px;
  height: 5px;
  background: ${({ theme }) => theme.colors.primary};
  border-radius: 1px;
  animation: ${equalizer} 1s ease-in-out infinite;
  animation-delay: ${({ delay }) => `${delay}s`};
`;

// Helper function to format time
const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
};

const AudioPlayer: React.FC = () => {
  const {
    currentTrack,
    isPlaying,
    togglePlayPause,
    progress,
    duration,
    volume,
    setVolume,
    seek,
    prevTrack,
    nextTrack
  } = useAudio();
  
  const { addToCart, isInCart } = useCart();
  const [isVisible, setIsVisible] = useState(false);
  const progressBarRef = useRef<HTMLDivElement>(null);
  
  // Show player when we have a current track
  useEffect(() => {
    if (currentTrack) {
      setIsVisible(true);
    }
  }, [currentTrack]);
  
  // Handle progress bar click
  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!progressBarRef.current || !duration) return;

    const rect = progressBarRef.current.getBoundingClientRect();
    const clickPosition = (e.clientX - rect.left) / rect.width;
    const newTime = clickPosition * duration;

    seek(newTime);
  };
  
  // Handle volume change
  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
  };
  
  // Handle adding the current track to cart
  const handleAddToCart = () => {
    if (currentTrack) {
      addToCart({
        id: currentTrack.id,
        title: currentTrack.title,
        artist: currentTrack.artist,
        url: currentTrack.url,
        coverArt: currentTrack.coverArt,
        price: 29.99
      });
    }
  };

  // Calculate progress width percentage
  const progressWidth = duration > 0 ? (progress / duration) * 100 : 0;
  
  return (
    <AnimatePresence>
      {isVisible && (
        <PlayerContainer
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        >
          <PlayerContent>
            {currentTrack && (
              <>
                <TrackInfo>
                  <AlbumArt isPlaying={isPlaying}>
                    {isPlaying && <RippleEffect />}
                    {(currentTrack.url?.includes('acetates/') || 
                      currentTrack.url?.includes('/acetates/')) ? (
                      <div className="acetate-container">
                        <AcetatePlaceholder size={50} animated={false} />
                      </div>
                    ) : (
                      <div className="regular-image">
                        <ImageWithFallback
                          src={currentTrack.coverArt || ''}
                          alt={currentTrack.title}
                          audioUrl={currentTrack.url}
                        />
                      </div>
                    )}
                  </AlbumArt>
                  
                  <NowPlaying>
                    <TrackTitle>{currentTrack.title}</TrackTitle>
                    <ArtistName>{currentTrack.artist}</ArtistName>
                  </NowPlaying>
                  
                  <Equalizer isPlaying={isPlaying}>
                    {EQUALIZER_BAR_INDICES.map(i => (
                      <EqualizerBar key={i} delay={i * 0.2} />
                    ))}
                  </Equalizer>
                  
                                  </TrackInfo>
                
                <Controls>
                  <SkipButton onClick={prevTrack} aria-label="Previous track">
                    <svg viewBox="0 0 24 24">
                      <path d="M6 6h2v12H6zm3.5 6l8.5 6V6z" />
                    </svg>
                  </SkipButton>
                  
                  <PlayButton 
                    isPlaying={isPlaying}
                    onClick={togglePlayPause}
                    aria-label={isPlaying ? "Pause" : "Play"}
                  >
                    {isPlaying ? (
                      <svg viewBox="0 0 24 24">
                        <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    )}
                  </PlayButton>
                  
                  <SkipButton onClick={nextTrack} aria-label="Next track">
                    <svg viewBox="0 0 24 24">
                      <path d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z" />
                    </svg>
                  </SkipButton>
                </Controls>
                
                <ProgressContainer>
                  <ProgressBar 
                    ref={progressBarRef}
                    onClick={handleProgressClick}
                  >
                    <Progress width={progressWidth} isPlaying={isPlaying} />
                  </ProgressBar>
                  
                  <TimeDisplay>
                    <span>{formatTime(progress)}</span>
                    <span>
                      {formatTime(duration)}
                    </span>
                  </TimeDisplay>
                </ProgressContainer>
                
                <VolumeControls>
                  <VolumeIcon>
                    {volume === 0 ? (
                      <svg viewBox="0 0 24 24">
                        <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" />
                      </svg>
                    ) : volume < 0.5 ? (
                      <svg viewBox="0 0 24 24">
                        <path d="M18.5 12c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM5 9v6h4l5 5V4L9 9H5z" />
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24">
                        <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
                      </svg>
                    )}
                  </VolumeIcon>
                  
                  <VolumeSlider
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={volume}
                    onChange={handleVolumeChange}
                    aria-label="Volume control"
                  />
                </VolumeControls>
                
                <CartButton 
                  inCart={currentTrack ? isInCart(currentTrack.id) : false}
                  onClick={handleAddToCart}
                  aria-label={currentTrack && isInCart(currentTrack.id) ? "Added to cart" : "Add to cart"}
                  title={currentTrack && isInCart(currentTrack.id) ? "Added to cart" : "Add to cart"}
                >
                  {currentTrack && isInCart(currentTrack.id) ? (
                    <svg viewBox="0 0 24 24">
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24">
                      <path d="M11 9h2V6h3V4h-3V1h-2v3H8v2h3v3zm-4 9c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zm10 0c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2zm-8.9-5h7.45c.75 0 1.41-.41 1.75-1.03l3.86-7.01L19.41 4l-3.86 7H8.53L4.27 2H1v2h2l3.6 7.59-1.35 2.44C4.52 15.37 5.48 17 7 17h12v-2H7l1.1-2z" />
                    </svg>
                  )}
                </CartButton>
              </>
            )}
          </PlayerContent>
        </PlayerContainer>
      )}
    </AnimatePresence>
  );
};

export default AudioPlayer;